if (!customElements.get('search-dropdown')) {
  const escapeHtml = str =>
    String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  class SearchDropdown extends window.BAO.CustomElement({
    SuperClass: HTMLDialogElement,
  }) {
    constructor () {
      super()
      this.eventBus = window.theme.autocomplete.eventBus
      this.events = window.theme.autocomplete.events
      this.config = window.theme.autocomplete.config

      this.lastResponse = null

      this.debouncedHandleInput = window.BAO.utils.debounce(
        this.handleInput.bind(this),
        this.config.debounceMs,
      )
    }

    connectedCallback () {
      super.connectedCallback()
      this.subscribeToEvents()

      const snapshot = window.theme.autocomplete.stateLayer?.state
      if (snapshot) this.onResponseUpdated(snapshot)
    }

    setupListeners () {
      super.setupListeners()

      this.externalInput = this.externalEls.input?.element
      this.externalClear = this.externalEls.clear?.element

      this.listeners.add(
        this.els.input.element,
        'input',
        this.debouncedHandleInput,
      )
      if (this.externalInput) {
        this.listeners.add(
          this.externalInput,
          'input',
          this.debouncedHandleInput,
        )
      }

      this.listeners.add(
        this.els.close.element,
        'click',
        this.handleClose.bind(this),
      )
      this.listeners.add(this, 'click', event => {
        if (event.target === this) this.handleClose(event)
      })
      this.listeners.add(document, 'keydown', event => {
        if (event.key === 'Escape' && this.hasAttribute('open')) {
          this.handleClose(event)
        }
      })

      this.listeners.add(
        this.els.clear.element,
        'click',
        this.handleClear.bind(this),
      )
      if (this.externalClear) {
        this.listeners.add(
          this.externalClear,
          'click',
          this.handleClear.bind(this),
        )
      }

      const onKeywordClick = this.handleKeywordClick.bind(this)
      this.listeners.add(
        this.els.resultsSuggestions.element,
        'click',
        onKeywordClick,
      )
      this.listeners.add(
        this.els.initialSuggestions.element,
        'click',
        onKeywordClick,
      )

      this.listeners.add(
        this.els.resultsProducts.element,
        'click',
        this.handleProductCardClick.bind(this),
      )

      this.listeners.add(
        this.els.form.element,
        'submit',
        this.handleSubmit.bind(this),
      )
    }

    subscribeToEvents () {
      this.eventBus.subscribe(
        this.events.STATE.RESPONSE_UPDATED,
        this.onResponseUpdated.bind(this),
      )

      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.DRAWER.CLOSED,
        this.handleClose.bind(this),
      )

      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.DRAWER.OPENED,
        this.handleClose.bind(this),
      )
    }

    onResponseUpdated (response) {
      this.lastResponse = response
      const view = this.pickView(response)
      this.setView(view)

      if (view === 'initial') {
        this.renderRecentSearches(response.recentSearches)
        this.renderSuggestions(
          this.els.initialSuggestions.element,
          response.popularSearches,
          { skipIfPresent: true },
        )
      } else if (view === 'results') {
        this.renderProducts(response.products, response.activeTerm)
        this.renderKeywords(
          this.els.resultsSuggestions.element,
          response.keywords,
        )

        this.renderCategories(response.categories)
      } else if (view === 'no-results') {
        this.renderNoResults(response.activeTerm)
      }
    }

    pickView (response) {
      if (response.fetching) return 'initial'

      const hasTerm = response.activeTerm?.length > 1
      const hasHits = response.products?.hits?.length > 0

      if (hasTerm && hasHits) return 'results'
      if (hasTerm && !hasHits) return 'no-results'
      return 'initial'
    }

    setView (view) {
      const views = {
        initial: this.els.initialView.element,
        results: this.els.resultsView.element,
        'no-results': this.els.noResultsView.element,
      }

      Object.entries(views).forEach(([name, element]) => {
        if (name === view) {
          element.removeAttribute('hidden')
        } else {
          element.setAttribute('hidden', '')
        }
      })
    }

    renderProducts (products, activeTerm) {
      const list = this.els.resultsProducts.element.querySelector('ul')
      list.innerHTML = ''

      this.els.resultsProductsViewAll.element.setAttribute(
        'href',
        `/search?q=${encodeURIComponent(activeTerm)}`,
      )
      this.els.resultsProductsHeading.element.textContent = `Product results (${products.total})`

      products.hits.forEach((product, index) => {
        list.insertAdjacentHTML(
          'beforeend',
          window.theme.autocomplete.templates.ProductCard({
            product,
            index,
            type: 'autocomplete',
          }),
        )
      })
    }

    renderSuggestions (targetEl, keywords, { skipIfPresent = false } = {}) {
      if (
        skipIfPresent &&
        targetEl.querySelectorAll('.hd-SearchDropdown_SuggestionsLink').length
      ) {
        return
      }

      if (!keywords.length) {
        targetEl.innerHTML = ''
        return
      }

      targetEl.innerHTML = this.templatePopularSuggestions(keywords)
    }

    renderKeywords (targetEl, keywords, { skipIfPresent = false } = {}) {
      if (
        skipIfPresent &&
        targetEl.querySelectorAll('.hd-SearchDropdown_SuggestionsLink').length
      ) {
        return
      }

      if (!keywords.hits.length) {
        targetEl.innerHTML = ''
        return
      }

      targetEl.innerHTML = this.templateKeywords(keywords.hits)
    }

    renderRecentSearches (recentSearches) {
      if (!recentSearches?.length) {
        this.els.initialRecentSearches.element.innerHTML = ''
        return
      }

      this.els.initialRecentSearches.element.innerHTML = this.templateRecentSearches(
        recentSearches,
      )
      this.els.initialRecentSearches.element.removeAttribute('hidden')
    }

    renderCategories (categories) {
      if (!categories.length) {
        this.els.resultsCategories.element.innerHTML = ''
        return
      }
      this.els.resultsCategories.element.innerHTML = this.templateSearchCategories(
        categories,
      )
    }

    renderNoResults (activeTerm) {
      this.els.noResultsHeading.element.textContent = `Sorry! No results for ${activeTerm}`
    }

    handleInput (event) {
      const query = event.target.value.trim()

      if (event.target.value.length > 0) {
        this.els.clear.element.removeAttribute('hidden')
        this.externalClear?.removeAttribute('hidden')
      } else {
        this.els.clear.element.setAttribute('hidden', '')
        this.externalClear?.setAttribute('hidden', '')
      }

      this.eventBus.publish(this.events.UI.QUERY_UPDATED, { query })
    }

    handleClear () {
      this.els.input.element.value = ''
      if (this.externalInput) this.externalInput.value = ''
      this.externalClear?.setAttribute('hidden', '')
      this.els.clear.element.setAttribute('hidden', '')
      this.eventBus.publish(this.events.UI.QUERY_UPDATED, { query: '' })
    }

    handleClose () {
      if (!this.open) return

      this.els.input.element.value = ''
      this.eventBus.publish(this.events.UI.QUERY_UPDATED, { query: '' })
      this.close()
      document.body.style.overflow = ''

      const customEvent = new CustomEvent('headerSearch::closed', {
        bubbles: true,
      })
      window.dispatchEvent(customEvent)
    }

    handleKeywordClick (event) {
      if (!event.target.closest('.hd-SearchDropdown_SuggestionsLink')) return

      const response = this.lastResponse
      if (!response) return

      this.eventBus.publish(this.events.TRACKING.KEYWORD_CLICKED, {
        request: { query: response.activeTerm },
        results: response.products,
      })
    }

    handleProductCardClick (event) {
      const productCard = event.target.closest('product-card')
      const target = event.target?.href || false
      if (!productCard || !target) return

      this.eventBus.publish(this.events.TRACKING.PRODUCT_CLICKED, {
        productId: productCard.getAttribute('data-product-id'),
        productUrl: productCard.getAttribute('data-product-url'),
      })
    }

    handleSubmit (event) {
      this.eventBus.publish(this.events.TRACKING.SUBMIT_SEARCH, {
        query: this.els.input.element.value,
      })
    }

    templatePopularSuggestions (suggestions) {
      return `
        <div class="hd-SearchDropdown_SuggestionsHeader">
            <div class="hd-SearchDropdown_SuggestionsHeading">
              Search suggestions (${suggestions.length})
            </div>
          </div>

          <ul class="hd-SearchDropdown_SuggestionsList">
            ${suggestions
              .map(
                suggestion => `
              <li class="hd-SearchDropdown_SuggestionsItem">
                <a href="/search?q=${encodeURIComponent(
                  suggestion.query,
                )}" class="hd-SearchDropdown_SuggestionsLink">
                  ${escapeHtml(suggestion.query)}
                </a>
              </li>
            `,
              )
              .join('')}
          </ul>
        `
    }

    templateKeywords (suggestions) {
      return `
        <div class="hd-SearchDropdown_SuggestionsHeader">
            <div class="hd-SearchDropdown_SuggestionsHeading">
              Search suggestions (${suggestions.length})
            </div>
          </div>

          <ul class="hd-SearchDropdown_SuggestionsList">
            ${suggestions
              .map(
                suggestion => `
              <li class="hd-SearchDropdown_SuggestionsItem">
                <a href="/search?q=${encodeURIComponent(
                  suggestion.keyword,
                )}" class="hd-SearchDropdown_SuggestionsLink">
                  ${escapeHtml(suggestion.keyword)}
                </a>
              </li>
            `,
              )
              .join('')}
          </ul>
        `
    }

    templateRecentSearches (recentSearches) {
      return `
        <div class="hd-SearchDropdown_SuggestionsHeader">
            <div class="hd-SearchDropdown_SuggestionsHeading">
              Recent searches (${recentSearches.length})
            </div>
          </div>

          <ul class="hd-SearchDropdown_SuggestionsList">
            ${recentSearches
              .map(
                recent => `
              <li class="hd-SearchDropdown_SuggestionsItem">
                <a href="/search?q=${encodeURIComponent(
                  recent,
                )}" class="hd-SearchDropdown_SuggestionsLink">
                  ${escapeHtml(recent)}
                </a>
              </li>
            `,
              )
              .join('')}
          </ul>
        `
    }

    templateSearchCategories (categories) {
      return `
        <div class="hd-SearchDropdown_SuggestionsHeader">
            <div class="hd-SearchDropdown_SuggestionsHeading">
              Collection results (${categories.length})
            </div>
          </div>

          <ul class="hd-SearchDropdown_SuggestionsList hd-SearchDropdown_SuggestionsList-vertical">
            ${categories
              .map(
                category => `
              <li class="hd-SearchDropdown_SuggestionsItem">
                <a href="${escapeHtml(
                  category.url,
                )}" class="hd-SearchDropdown_SuggestionsLink">
                  ${escapeHtml(category.name)}

                  <svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style="transform: rotate(-90deg);"><rect x="9.19238" width="1" height="7" transform="rotate(45 9.19238 0)" fill="currentColor"></rect><rect x="0.707031" width="7" height="1" transform="rotate(45 0.707031 0)" fill="currentColor"></rect></svg>
                </a>
              </li>
            `,
              )
              .join('')}
          </ul>
        `
    }
  }

  customElements.define('search-dropdown', SearchDropdown, {
    extends: 'dialog',
  })
}
